import { useTranslation } from "react-i18next";
import { usePageMeta } from "@/hooks/usePageMeta";
import styles from "./ContentPage.module.css";

const PARAMS: Array<{ name: string; required: string; example: string; descKey: string }> = [
  { name: "title", required: "yes", example: "Sommerfest", descKey: "paramTitle" },
  { name: "date", required: "yes", example: "2026-07-12T18:00:00+02:00", descKey: "paramDate" },
  { name: "items", required: "no", example: "Salat,Brot,Getr%C3%A4nke", descKey: "paramItems" },
  { name: "deadline", required: "no", example: "2026-07-11T12:00:00+02:00", descKey: "paramDeadline" },
  { name: "location", required: "no", example: "Garten%20hinten", descKey: "paramLocation" },
  { name: "lang", required: "no", example: "de", descKey: "paramLang" },
];

export function AgentsGuide() {
  const { t } = useTranslation();
  usePageMeta({
    title: t("pageMeta.agentsTitle"),
    description: t("pageMeta.agentsDescription"),
  });
  const origin =
    typeof window !== "undefined" ? window.location.origin : "https://brings-mit.com";

  const linkExample =
    `${origin}/new?title=Sommerfest&date=2026-07-12T18%3A00%3A00%2B02%3A00` +
    `&items=Salat%2CBrot%2CGetr%C3%A4nke&deadline=2026-07-11T12%3A00%3A00%2B02%3A00` +
    `&location=Garten&lang=de`;

  return (
    <article className={styles.page}>
      <div>
        <h1>{t("agents.title")}</h1>
        <p className={styles.lead}>{t("agents.lead")}</p>
      </div>

      <section>
        <h2>{t("agents.canDoTitle")}</h2>
        <ul>
          <li>
            <strong>{t("agents.canCreateEventTitle")}</strong> —{" "}
            <code>POST /api/v1/events</code>. {t("agents.canCreateEventBody")}
          </li>
          <li>
            <strong>{t("agents.canManageItemsTitle")}</strong> —{" "}
            <code>
              POST / PUT / PATCH / DELETE /api/v1/events/&#123;shortcut&#125;/admin/
              &#123;adminKey&#125;/items…
            </code>
            . {t("agents.canManageItemsBody")}
          </li>
          <li>
            <strong>{t("agents.canReadTitle")}</strong> —{" "}
            <code>GET /api/v1/events/&#123;shortcut&#125;</code> +{" "}
            <code>GET /api/v1/events/&#123;shortcut&#125;/items</code>.{" "}
            {t("agents.canReadBody")}
          </li>
        </ul>
      </section>

      <section>
        <h2>{t("agents.cannotDoTitle")}</h2>
        <ul>
          <li>
            <strong>{t("agents.cannotJoinTitle")}</strong> —{" "}
            {t("agents.cannotJoinBody")}
          </li>
          <li>
            <strong>{t("agents.cannotClaimTitle")}</strong> —{" "}
            {t("agents.cannotClaimBody")}
          </li>
          <li>
            <strong>{t("agents.cannotCommentTitle")}</strong> —{" "}
            {t("agents.cannotCommentBody")}
          </li>
        </ul>
        <p className={styles.disclaimer}>{t("agents.cannotDoRationale")}</p>
      </section>

      <section>
        <h2>{t("agents.deepLinkTitle")}</h2>
        <p>{t("agents.deepLinkBody")}</p>
        <ol>
          <li>{t("agents.step1")}</li>
          <li>{t("agents.step2")}</li>
          <li>{t("agents.step3")}</li>
        </ol>

        <h3>{t("agents.paramsTitle")}</h3>
        <p>{t("agents.paramsLead")}</p>
        <table className={styles.metaTable}>
          <thead>
            <tr>
              <th>{t("agents.colName")}</th>
              <th>{t("agents.colRequired")}</th>
              <th>{t("agents.colExample")}</th>
              <th>{t("agents.colDesc")}</th>
            </tr>
          </thead>
          <tbody>
            {PARAMS.map((p) => (
              <tr key={p.name}>
                <td><code>{p.name}</code></td>
                <td>{t(`agents.${p.required === "yes" ? "required" : "optional"}`)}</td>
                <td><code>{p.example}</code></td>
                <td>{t(`agents.${p.descKey}`)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <h3>{t("agents.exampleTitle")}</h3>
        <pre className={styles.code}>{linkExample}</pre>
      </section>

      <section>
        <h2>{t("agents.mcpTitle")}</h2>
        <p>{t("agents.mcpLead")}</p>

        <h3>{t("agents.mcpConnectTitle")}</h3>
        <p>{t("agents.mcpConnectBody")}</p>
        <pre className={styles.code}>
{`{
  "mcpServers": {
    "brings-mit": {
      "url": "${origin}/mcp",
      "transport": "streamable-http"
    }
  }
}`}
        </pre>

        <h3>{t("agents.mcpAuthTitle")}</h3>
        <p>{t("agents.mcpAuthBody")}</p>

        <h3>{t("agents.mcpToolsTitle")}</h3>
        <ul>
          <li><strong>{t("agents.mcpToolsAdminTitle")}</strong> — {t("agents.mcpToolsAdminBody")}</li>
          <li><strong>{t("agents.mcpToolsUserTitle")}</strong> — {t("agents.mcpToolsUserBody")}</li>
          <li><strong>{t("agents.mcpToolsPublicTitle")}</strong> — {t("agents.mcpToolsPublicBody")}</li>
        </ul>
        <p className={styles.disclaimer}>{t("agents.mcpRationale")}</p>
      </section>

      <section>
        <h2>{t("agents.discoveryTitle")}</h2>
        <ul>
          <li><code>/llms.txt</code> — {t("agents.discoveryLlms")}</li>
          <li><code>/api/openapi.json</code> — {t("agents.discoveryOpenapi")}</li>
          <li><code>/mcp</code> — {t("agents.discoveryMcp")}</li>
        </ul>
      </section>
    </article>
  );
}
